/** Accounts (DEPLOYMENT.md M2, R4) — optional sign-in for the studio.
 *
 * The backend serves a PUBLIC client config for each provider; when neither
 * is configured this module resolves to `null` and the studio stays in
 * single-user local mode. On sign-in, the provider's access/ID token is
 * mirrored into a `graphmind_id_token` cookie so EVERY request path (fetch,
 * SSE, pdf.js byte-ranges, <img>) carries the identity — the backend verifies
 * the JWT and scopes all data to the signed-in subject.
 *
 * Two provider modules exist behind one seam, and only ONE of them is live.
 * `init()` below tries Supabase's config first and falls back to Firebase's,
 * so Supabase always wins wherever it is configured — which is everywhere the
 * product runs. Firebase sign-in is reachable NOWHERE today: the hosted studio
 * gets a Supabase config, and Firebase's authorized-domain list does not
 * include `app.graphview.org`, so its SDK would reject the origin even if the
 * fallback were taken. The Firebase secrets are still set in production and
 * `/api/auth/firebase/config` still answers, so the path is unreachable rather
 * than absent. Both modules share this file's `AccountUser`/`Accounts` shape
 * and both feed the SAME cookie lifecycle in `@/lib/auth-token` — that module
 * is entirely provider-agnostic.
 */

import { initFirebaseAccounts } from "@/lib/accounts-firebase";
import { initSupabaseAccounts } from "@/lib/accounts-supabase";

export interface AccountUser {
  uid: string;
  email: string | null;
  displayName: string | null;
  photoURL: string | null;
}

/** Result of an email/password sign-up. When email confirmation is required,
 * Supabase returns NO session — the user must click the verification link
 * before they're actually signed in, so callers must show a "check your email"
 * state rather than treating this as a completed sign-in. */
export interface SignUpResult {
  user: AccountUser;
  needsVerification: boolean;
}

export interface Accounts {
  /** Which backend is driving sign-in. The sign-in UI branches its form on
   * this: Supabase gets email/password + Google; Firebase stays Google-only. */
  provider: "firebase" | "supabase";
  /** Google sign-in. Firebase pops up an OAuth window and resolves with the
   * signed-in user. Supabase redirects the whole page (`signInWithOAuth`) —
   * there's no user to return here; the app reloads at `redirectTo` and
   * `onUser`/the cookie pick up the restored session, hence `void`. */
  signInWithGoogle: () => Promise<AccountUser | void>;
  /** Email/password sign-in. Only present for Supabase (`undefined` under
   * Firebase, which has no password provider configured). */
  signInWithPassword?: (email: string, password: string) => Promise<AccountUser>;
  /** Email/password sign-up. Only present for Supabase. Returns whether email
   * verification is still pending (no session yet). */
  signUp?: (email: string, password: string) => Promise<SignUpResult>;
  /** Re-send the sign-up verification email (Supabase only). */
  resendVerification?: (email: string) => Promise<void>;
  /** Start the forgot-password flow: emails a recovery link that lands back on
   * the app and fires `onPasswordRecovery` (Supabase only). */
  resetPassword?: (email: string) => Promise<void>;
  /** Set a new password for the CURRENT session — used both by the recovery
   * landing and Account settings (Supabase only). */
  updatePassword?: (password: string) => Promise<void>;
  /** Change the account email; Supabase sends a re-verification mail to the new
   * address (Supabase only). */
  updateEmail?: (email: string) => Promise<void>;
  /** Fires when the browser returns from a password-recovery email link, so the
   * app can prompt for a new password (Supabase only). Returns an unsubscribe. */
  onPasswordRecovery?: (cb: () => void) => () => void;
  signOut: () => Promise<void>;
  /** Subscribe to auth state; fires immediately with the current user. */
  onUser: (cb: (user: AccountUser | null) => void) => () => void;
}

let cached: Promise<Accounts | null> | null = null;

/** Resolve the accounts API once per session (null = accounts not configured
 * for either provider). */
export function accounts(): Promise<Accounts | null> {
  cached ??= init();
  return cached;
}

async function init(): Promise<Accounts | null> {
  const supabase = await initSupabaseAccounts();
  if (supabase) return supabase;
  return initFirebaseAccounts();
}
